/**
 * GenerateBand — axis chip selector, competitors (Comparaison: the tracked competitors with their logo,
 * one or several picked, none = all), topic checkboxes, language, count. (Generation by persona is its
 * own composer source since 2026-11-02: PersonaBand.)
 * On « Générer »: calls brandQueriesApi.regenerate, then calls onDraft(lines).
 * 429 GEN_COOLDOWN → calls onCooldown(until).
 * Other errors → inline failure line.
 *
 * @see B-popup-frontend.md step 5/6, Files and props § GenerateBand
 */
import { useEffect, useMemo, useState } from 'react';
import { Box, Chip, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import type { BrandQueryAxis, TrackedCompetitor } from '@traaker/shared';
import { PROMPT_LANGUAGES } from '@traaker/shared';
import type { GenState } from './composerModel';
import { GEN_COOLDOWN_MS, cooldownSecondsLeft } from './composerModel';
import { AXIS_COLOR } from './promptStudioModel';
import { brandQueriesApi } from '../../services/brand-queries';
import { competitorsApi } from '../../services/competitors';
import DomainFavicon from '../competitors/DomainFavicon';
import { Pick } from './Pick';

export interface GenerateBandProps {
  gen: GenState;
  brandId: string;
  axes: readonly BrandQueryAxis[];
  vocabByAxis: Record<BrandQueryAxis, string[]>;
  /** The brand's prompt languages (working language first); one chip each when > 1. */
  languages: { code: string; label: string }[];
  cooldownUntil: number | null;
  done: boolean;
  onChange: (gen: GenState) => void;
  /** Called with proposal texts on success; parent updates draft + calls onVerifier. */
  onDraft: (lines: string[]) => void;
  /** Called on 429 GEN_COOLDOWN with the new cooldownUntil timestamp. */
  onCooldown: (until: number) => void;
}

const AXIS_FR: Record<BrandQueryAxis, string> = {
  // i18n: promptStudio.axis.*
  narrative: 'Image',
  authority: 'Autorité',
  referral: 'Recommandation',
  comparison: 'Comparaison',
};

const VOCAB_CAPTION: Record<BrandQueryAxis, string> = {
  // i18n: promptStudio.gen.vocabCaption.*
  narrative: 'Critères',
  authority: 'Thèmes',
  referral: 'Cas d\'usage',
  comparison: 'Critères',
};

export function GenerateBand({
  gen,
  brandId,
  axes,
  vocabByAxis,
  languages,
  cooldownUntil,
  done,
  onChange,
  onDraft,
  onCooldown,
}: GenerateBandProps) {
  const { i18n } = useTranslation();
  const [langPickOpen, setLangPickOpen] = useState(false);
  const [langSearch, setLangSearch] = useState('');
  const languageOptions = useMemo(() => generationLanguages(languages, i18n.language), [languages, i18n.language]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Comparaison: the brand's tracked competitors (null = not loaded yet), fetched on first use.
  const [competitors, setCompetitors] = useState<TrackedCompetitor[] | null>(null);
  const isComparison = gen.axis === 'comparison';
  useEffect(() => {
    if (!isComparison || competitors !== null) return;
    let live = true;
    competitorsApi.list(brandId)
      .then((list) => { if (live) setCompetitors(list ?? []); })
      .catch(() => { if (live) setCompetitors([]); });
    return () => { live = false; };
  }, [isComparison, competitors, brandId]);

  const vocab = vocabByAxis[gen.axis] ?? [];
  const picked = gen.competitors.filter((c) => (competitors ?? []).some((t) => t.name === c));

  const handleRun = async () => {
    if (loading) return;
    setLoading(true);
    setError(false);
    try {
      const res = await brandQueriesApi.regenerate(brandId, gen.axis, {
        topics: gen.terms.filter(Boolean) as string[],
        count: gen.count,
        languages: [gen.language],
        ...(isComparison ? { competitors: picked } : {}),
        studio: true,
      });
      onCooldown(Date.now() + GEN_COOLDOWN_MS);
      const lines = (res.proposals ?? []).map((p) => p.text).filter(Boolean) as string[];
      onDraft(lines);
    } catch (err: unknown) {
      const e = err as { body?: { code?: string; retryAfterSeconds?: number } };
      if (e?.body?.code === 'GEN_COOLDOWN' && typeof e.body.retryAfterSeconds === 'number') {
        onCooldown(Date.now() + e.body.retryAfterSeconds * 1000);
      } else {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 0.5 }}>
      {/* Axis chips */}
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
        {axes.map((a) => (
          <Chip
            key={a}
            label={AXIS_FR[a]}
            size="small"
            onClick={() => onChange({ ...gen, axis: a, terms: [] })}
            sx={{
              fontSize: 12,
              bgcolor: gen.axis === a ? `${AXIS_COLOR[a]}20` : 'rgba(0,0,0,.05)',
              color: gen.axis === a ? AXIS_COLOR[a] : 'text.secondary',
              border: `1px solid ${gen.axis === a ? AXIS_COLOR[a] : 'transparent'}`,
              fontWeight: gen.axis === a ? 600 : 400,
            }}
          />
        ))}
      </Box>

      {/* Comparaison: the competitors to compare the brand with (one or several; none = all) */}
      {isComparison && (
        <>
          <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
            {/* i18n: promptStudio.gen.competitors */}
            Concurrents
            {competitors !== null && (
              <Box component="span" sx={{ ml: 1, color: 'text.disabled' }}>
                {competitors.length === 0
                  ? 'aucun concurrent suivi, comparaison avec « ses alternatives »' // i18n: promptStudio.gen.noCompetitor
                  : picked.length === 0
                    ? `aucun choisi, réparti sur les ${competitors.length}` // i18n: promptStudio.gen.none
                    : `${picked.length} choisi${picked.length > 1 ? 's' : ''}`}
              </Box>
            )}
          </Box>
          {competitors !== null && competitors.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }} data-ps-control="gen-competitors">
              {competitors.map((c) => {
                const active = picked.includes(c.name);
                return (
                  <Chip
                    key={c.domain}
                    size="small"
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <DomainFavicon domain={c.domain} name={c.name} size={16} />
                        {c.name}
                      </Box>
                    }
                    onClick={() =>
                      onChange({
                        ...gen,
                        competitors: active ? picked.filter((n) => n !== c.name) : [...picked, c.name],
                      })
                    }
                    sx={{
                      fontSize: 11.5,
                      bgcolor: active ? 'rgba(124,58,237,.12)' : 'rgba(0,0,0,.05)',
                      color: active ? '#7c3aed' : 'text.secondary',
                      border: `1px solid ${active ? 'rgba(124,58,237,.3)' : 'transparent'}`,
                      '& .MuiChip-label': { pl: 0.75 },
                    }}
                  />
                );
              })}
            </Box>
          )}
        </>
      )}

      {/* Vocabulary caption */}
      <Box sx={{ fontSize: 12, color: 'text.secondary' }}>
        {VOCAB_CAPTION[gen.axis]}
        {vocab.length === 0 ? (
          <Box component="span" sx={{ ml: 1, color: 'text.disabled' }}>
            {/* i18n: promptStudio.gen.noVocab */}
            aucun terme dans Brand Knowledge
          </Box>
        ) : (
          <Box component="span" sx={{ ml: 1, color: 'text.disabled' }}>
            {/* i18n: promptStudio.gen.none */}
            {gen.terms.filter(Boolean).length === 0
              ? `aucun choisi, réparti sur les ${vocab.length}`
              : `${gen.terms.filter(Boolean).length} choisi${gen.terms.filter(Boolean).length > 1 ? 's' : ''}`}
          </Box>
        )}
      </Box>

      {/* Term chips */}
      {vocab.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {vocab.map((term) => {
            const active = gen.terms.includes(term);
            return (
              <Chip
                key={term}
                label={term}
                size="small"
                onClick={() => {
                  const next = active
                    ? gen.terms.filter((t) => t !== term)
                    : [...gen.terms, term];
                  onChange({ ...gen, terms: next });
                }}
                sx={{
                  fontSize: 11.5,
                  bgcolor: active ? 'rgba(124,58,237,.12)' : 'rgba(0,0,0,.05)',
                  color: active ? '#7c3aed' : 'text.secondary',
                  border: `1px solid ${active ? 'rgba(124,58,237,.3)' : 'transparent'}`,
                }}
              />
            );
          })}
        </Box>
      )}

      {/* Language — one per generation, any prompt language (owner, 2026-10-21) */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}>
          {/* i18n: promptStudio.gen.language */}
          Langue
        </Box>
        <Chip
          label={languageOptions.find((l) => l.code === gen.language)?.label ?? gen.language}
          size="small"
          onClick={() => { setLangSearch(''); setLangPickOpen(true); }}
          onDelete={() => { setLangSearch(''); setLangPickOpen(true); }}
          deleteIcon={<ExpandMoreIcon />}
          sx={{ fontSize: 12, bgcolor: 'rgba(124,58,237,.08)', color: '#7c3aed', border: '1px solid rgba(124,58,237,.25)' }}
        />
      </Box>

      {/* Count buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ fontSize: 12, color: 'text.secondary', flexShrink: 0 }}>
          {/* i18n: promptStudio.gen.count */}
          Questions
        </Box>
        <ToggleButtonGroup
          value={gen.count}
          exclusive
          onChange={(_, v) => v && onChange({ ...gen, count: v })}
          size="small"
        >
          {([3, 5, 10] as const).map((n) => (
            <ToggleButton key={n} value={n} sx={{ fontSize: 12, px: 1.5, py: 0.375 }}>
              {n}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {/* Generate button */}
      <GenerateButton
        cooldownUntil={cooldownUntil}
        done={done}
        loading={loading}
        onRun={handleRun}
      />

      {error && (
        <Box sx={{ fontSize: 12, color: '#b45309' }}>
          {/* i18n: promptStudio.gen.failed */}
          La génération a échoué, réessayez.
        </Box>
      )}

      {/* Language picker overlay (searchable) */}
      <Pick
        open={langPickOpen}
        kind="language"
        selected={gen.language}
        onToggle={(code) => {
          onChange({ ...gen, language: code });
          setLangPickOpen(false);
        }}
        onClose={() => setLangPickOpen(false)}
        languageOptions={languageOptions}
        searchQuery={langSearch}
        onSearchChange={setLangSearch}
      />

    </Box>
  );
}

// ---------------------------------------------------------------------------
// GenerateButton — owns its own 1 s interval
// ---------------------------------------------------------------------------

export interface GenerateButtonProps {
  cooldownUntil: number | null;
  done: boolean;
  loading?: boolean;
  /** Not runnable yet (PersonaBand: no persona chosen). */
  disabled?: boolean;
  onRun: () => void;
}

export function GenerateButton({ cooldownUntil, done, loading = false, disabled: blocked = false, onRun }: GenerateButtonProps) {
  const [now, setNow] = useState(() => Date.now());

  const left = cooldownSecondsLeft(cooldownUntil, now);
  const isCooling = left > 0;

  // Rerender every second while cooling
  if (isCooling) {
    setTimeout(() => setNow(Date.now()), 1000);
  }

  const disabled = isCooling || loading || blocked;

  return (
    <Box
      component="button"
      onClick={!disabled ? onRun : undefined}
      disabled={disabled}
      data-seconds={left}
      sx={{
        px: 2.5,
        py: 0.875,
        borderRadius: 1.5,
        border: 'none',
        bgcolor: disabled ? 'rgba(0,0,0,.06)' : '#7c3aed',
        color: disabled ? 'text.disabled' : '#fff',
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        alignSelf: 'flex-start',
        '&:hover': !disabled ? { bgcolor: '#6d28d9' } : {},
        transition: 'background-color .15s',
      }}
    >
      {isCooling
        ? `Régénérer dans ${left} s` // i18n: promptStudio.gen.cooldown
        : loading
          ? 'Génération…'             // i18n: promptStudio.gen.loading
          : done
            ? 'Régénérer'            // i18n: promptStudio.gen.regenerate
            : 'Générer'              // i18n: promptStudio.gen.generate
      }
    </Box>
  );
}

/**
 * The Générer language list: the brand's languages first (working language first, as passed), then every
 * other prompt language (PROMPT_LANGUAGES, C4) by name in the UI language.
 */
export function generationLanguages(
  brandLanguages: { code: string; label: string }[],
  uiLang: string,
): { code: string; label: string }[] {
  const have = new Set(brandLanguages.map((l) => l.code));
  const name = (code: string) => {
    try {
      const n = new Intl.DisplayNames([uiLang || 'fr'], { type: 'language' }).of(code) ?? code;
      return n.charAt(0).toUpperCase() + n.slice(1);
    } catch {
      return code;
    }
  };
  const rest = PROMPT_LANGUAGES.filter((l) => !have.has(l.code))
    .map((l) => ({ code: l.code, label: name(l.code) }))
    .sort((a, b) => a.label.localeCompare(b.label, uiLang || 'fr'));
  return [...brandLanguages, ...rest];
}
